import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useUIContext } from '../../../../context/UIContext'
import { useDownloadFile } from '../../../../hooks/useDownloadFile'
import { Error } from '../../../../interfaces/error'

export type UseReportCSVDownloadParams<T = unknown> = {
    downloadFn: () => Promise<T>
    fileName: string
    contentType?: string
}

export const useReportCSVDownload = <T = unknown>({
    downloadFn,
    fileName,
    contentType = 'text/csv',
}: UseReportCSVDownloadParams<T>) => {
    const { t } = useTranslation('teamReports')
    const { download } = useDownloadFile()
    const { toastNotification } = useUIContext()

    const mutation = useMutation(
        async () => {
            const data = await downloadFn()
            await download(data as string, fileName, contentType)
        },
        {
            onSuccess: () => {
                toastNotification(t('loading report'), 'success')
            },
            onError: (error: Error) => {
                const errorMessage = error?.response?.data?.message || 'There was an error downloading the report.'
                toastNotification(t(errorMessage), 'error')
            },
        }
    )

    const onExportCSV = useCallback(
        () =>
            mutation.mutateAsync().catch(() => {
                // Error already handled in mutation's onError callback
            }),
        [mutation]
    )

    return {
        onExportCSV,
        exporting: mutation.isLoading,
        status: mutation.status,
        error: mutation.error,
        reset: mutation.reset,
    }
}
